import { CatalogOccasion, DateRule } from '../../services/occasion-catalog'
import { G_MONTHS } from '../../configs/names'
import { toFaDigits } from '../../helpers/digits'

// How a date rule reads in Persian, so a holiday can say why it lands where it
// does instead of only showing this year's date. Every shape handled here is
// one the catalog actually carries — see services/occasion-catalog.ts.

const WEEKDAYS: Record<string, string> = {
    saturday: 'شنبه',
    sunday: 'یکشنبه',
    monday: 'دوشنبه',
    tuesday: 'سه‌شنبه',
    wednesday: 'چهارشنبه',
    thursday: 'پنجشنبه',
    friday: 'جمعه',
}

const ORDINALS = ['', 'اولین', 'دومین', 'سومین', 'چهارمین', 'پنجمین']

export function ruleText(
    rule: DateRule | undefined,
    byId: Map<string, CatalogOccasion>,
): string {
    // No rule means the date is not calculable — a one-off, or a day only an
    // authority can name. Saying so is the honest answer, not silence.
    if (!rule) return 'بدون قاعدهٔ تکرار؛ تاریخ را مرجع رسمی اعلام می‌کند'

    if (rule.kind === 'fixed') {
        const day = `${toFaDigits(rule.day)} ${G_MONTHS[rule.month - 1]}`
        if (!rule.years) return `${day} هر سال`
        if (rule.years.everyNthYear > 1) {
            const n = `، هر ${toFaDigits(rule.years.everyNthYear)} سال یک‌بار`
            const from = rule.years.fromYear ? `، از سال ${toFaDigits(rule.years.fromYear)}` : ''
            return `${day}${n}${from}`
        }
        return rule.years.fromYear
            ? `${day} هر سال، از سال ${toFaDigits(rule.years.fromYear)}`
            : `${day} هر سال`
    }
    if (rule.kind === 'nth_weekday') {
        const nth = `${ORDINALS[rule.nth]} ${WEEKDAYS[rule.weekday]} ${G_MONTHS[rule.month - 1]}`
        const moved = !rule.offsetDays ? nth
            : rule.offsetDays > 0
                ? `${toFaDigits(rule.offsetDays)} روز پس از ${nth}`
                : `${toFaDigits(Math.abs(rule.offsetDays))} روز پیش از ${nth}`
        return rule.years
            ? `${moved}، هر ${toFaDigits(rule.years.everyNthYear)} سال یک‌بار`
            : moved
    }
    if (rule.kind === 'last_weekday') {
        return `آخرین ${WEEKDAYS[rule.weekday]} ${G_MONTHS[rule.month - 1]}`
    }
    if (rule.kind === 'weekday_before') {
        return `${WEEKDAYS[rule.weekday]}‌ پیش از ${toFaDigits(rule.day)} ${G_MONTHS[rule.month - 1]}`
    }
    if (rule.kind === 'chinese_lunar') {
        return `${toFaDigits(rule.day)} از ماه ${toFaDigits(rule.month)} تقویم قمری چین`
    }
    if (rule.kind === 'qingming') {
        return 'روز چینگ‌مینگ (۴ یا ۵ آوریل)'
    }

    // An offset hangs off Easter or off another occasion in the same country.
    const from = byId.get(rule.base)
    const base = rule.base === 'easter'
        ? 'عید پاک'
        : from?.names.fa ?? from?.names.en ?? rule.base
    if (rule.offsetDays === 0) return base
    const days = toFaDigits(Math.abs(rule.offsetDays))
    return rule.offsetDays > 0 ? `${days} روز پس از ${base}` : `${days} روز پیش از ${base}`
}
